import React from "react";

import * as Dialog from "@radix-ui/react-dialog";
import { Check, X } from "lucide-react";

import ConfirmNote from "./ConfirmNote";
import {
  FLOW,
  PROBLEM_KIND,
  REPLACE_DRAFT,
  TEAM_STEP,
  pendingDraftNote,
  pubLabel,
} from "./flowCopy";
import { dueLine } from "./freshness";
import {
  BUCKET_LABEL,
  BUCKET_STYLE,
  PlanRowState,
  RowActionKind,
  awaitingConfirm,
} from "./planBucket";
import { shortDate } from "./shared";
import { formatMoney } from "./SponsorPlansList";
import VersionHistory, { UploadProblem, reasonOf } from "./VersionHistory";
import { useAccountPlanVersions } from "../../Hooks/useAccountPlan";
import { uploadPath } from "../../Pages/MyPortfolioAccounts/AccountPlanCell";
import type { PortfolioAccountRow } from "../../Store/Reducers/MyPortfolioOverviewSlice";
import type {
  AccountPlanStatus,
  PlanActivityRow,
} from "../../Types/AccountPlan";

/**
 * The plan page's Upload tab, opened on the newest upload's problem (contract with
 * the plan page: `focus=problem` opens Rejected uploads and outlines the newest one).
 */
export const problemPath = (accountId: string) =>
  `${uploadPath(accountId)}&focus=problem`;

type MiniState = "done" | "now" | "pending";

/** Upload · Confirm plan · Review briefing · Publish: where an upload in flight is. */
export const miniSteps = (
  s: AccountPlanStatus,
  row?: PlanRowState
): { label: string; state: MiniState }[] => {
  const f = s.latest_file;
  const reading = f?.status === "processing" || (!!f && !s.viewer_ready);
  const confirming = awaitingConfirm(s);
  // Every AE section verified: Publish is the step, for whoever is looking.
  const p = s.progress;
  const publishing =
    row?.action?.kind === "publish" ||
    (!!p &&
      p.AE.owned > 0 &&
      p.AE.verified >= p.AE.owned &&
      (s.review_status === "draft" || s.review_status === "in_review"));
  const st = (done: boolean, now: boolean): MiniState =>
    done ? "done" : now ? "now" : "pending";
  return [
    // While it waits its turn or is parsed, Upload is still the step — as the plan
    // page's step bar says (board 01 steps 2–6).
    { label: TEAM_STEP.plan, state: st(!!f && !reading, !f || reading) },
    {
      label: TEAM_STEP.viewer,
      state: st(!!f && !reading && !confirming, confirming),
    },
    {
      label: TEAM_STEP.sponsor,
      state: st(publishing, !!f && !reading && !confirming && !publishing),
    },
    { label: TEAM_STEP.publish, state: st(false, publishing) },
  ];
};

/** The newest upload stopped: its problem, from the API or derived from the file. */
const problemOf = (
  s: AccountPlanStatus | undefined,
  journeys: Record<string, PlanActivityRow>
): UploadProblem | null => {
  const f = s?.latest_file;
  if (!f) return null;
  if (f.problem) return f.problem;
  if (
    f.status !== "failed" &&
    f.status !== "rejected" &&
    f.status !== "needs_review"
  )
    return null;
  // Older API without `problem`: the Activity row's reason, else a plain default.
  const row = journeys[f.id];
  return {
    state:
      f.status === "failed"
        ? "read_failed"
        : f.status === "rejected"
          ? "needs_fixing"
          : "needs_ok",
    reason: row?.reason || null,
    detail: row?.error || null,
  };
};

interface Props {
  account: PortfolioAccountRow | null;
  status?: AccountPlanStatus;
  row?: PlanRowState;
  /** Activity rows by file id, read only for the plain reason an upload stopped. */
  journeys: Record<string, PlanActivityRow>;
  /** The viewer is the account's AE (or an admin acting as one): may upload and fix. */
  canUpload: boolean;
  /** The plan page lets this viewer in (they're on the account, or an admin). */
  canOpen: boolean;
  onClose: () => void;
  /** Routes a row action (verify, publish, draft, briefing…) as the list does. */
  onAction: (kind: RowActionKind) => void;
  /** Today's upload flow (the plan page's upload step). */
  onUpload: () => void;
  /**
   * "Replace this draft" (a confirmed draft isn't published): the plan page's
   * upload step with the replace confirm open. Only the AE's panel shows it.
   */
  onReplaceDraft?: () => void;
  /** The account plan page where it would land (NAV-01). */
  openPlan: () => void;
  /**
   * "Go to the problem": the plan page's Upload tab at `problemPath(id)`. When a
   * caller doesn't pass it, the upload step opens without the focus.
   */
  openProblem?: () => void;
  /** Unused: the panel's history carries no buttons (kept for callers). */
  openViewer?: (fileId: string) => void;
  parseCalendarDate: (v: string) => Date;
}

/** The panel's screen-reader description (F-UF-F30). */
export const PANEL_DESCRIPTION =
  "What the sponsor has, where the newest upload stands and the version history for this account.";

const CARD = "rounded-xl border px-4 py-3";
const KICKER = "text-[11px] font-semibold uppercase tracking-wide";
const BTN =
  "inline-flex h-10 flex-1 items-center justify-center rounded-lg px-3.5 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:cursor-not-allowed";
const OUTLINED = `${BTN} border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:border-gray-100 disabled:bg-gray-50 disabled:text-gray-400`;
const FILLED = `${BTN} bg-[#9333EA] text-white hover:bg-[#7E22CE] disabled:bg-gray-200 disabled:text-gray-500`;
const NO_ACCESS = FLOW.notOnTeamWhy;

/**
 * One account at a glance (upload-flow Phase 1, 2026-10-29): opens from its row in
 * the Plans list. Cards carry information only — what the sponsor reads, what's
 * wrong with the newest upload and why (or where it stands) — and the footer holds
 * exactly two actions: the published plan, and the one thing to do next.
 */
const AccountPlanPanel: React.FC<Props> = ({
  account,
  status,
  row,
  journeys,
  canUpload,
  canOpen,
  onClose,
  onAction,
  onUpload,
  onReplaceDraft,
  openPlan,
  openProblem,
  parseCalendarDate,
}) => {
  // Version metadata is readable organization-wide (like Activity): the panel works
  // for accounts the viewer isn't on, too.
  const { data: overview } = useAccountPlanVersions(account?.customer_id);
  const m = status?.meddpicc;
  const sub = account
    ? [
        account.revenue != null ? `${formatMoney(account.revenue)} ARR` : null,
        account.renewal_date
          ? `Renews ${parseCalendarDate(
              account.renewal_date
            ).toLocaleDateString(undefined, {
              month: "short",
              year: "numeric",
            })}`
          : null,
        account.industry,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  const latest = status?.latest_file || null;
  const published = status?.published || null;
  // Phase 3: the AE's last upload was refused before reading (newer than any upload).
  // Only the AE's panel shows it; for anyone else nothing happened to the plan.
  const rejection = canUpload ? status?.last_rejection || null : null;
  const pendingDraft = canUpload ? status?.pending_draft || null : null;
  // Q9 (Sushant, 2026-10-31): a stopped first upload with nothing published shows
  // anyone but the AE only that the AE is on it: no reason, no file name.
  const stoppedFirst =
    !canUpload && !published && !!latest && !!problemOf(status, journeys);
  const problem =
    rejection || stoppedFirst ? null : problemOf(status, journeys);
  const latestFile = latest
    ? overview?.files.find(f => f.id === latest.id)
    : undefined;
  const fileName =
    latestFile?.filename || (latest && journeys[latest.id]?.filename);
  const problemReason = problem
    ? problem.reason ||
      (latestFile ? reasonOf(latestFile, journeys[latestFile.id]) : null) ||
      (problem.state === "read_failed"
        ? "We couldn't read the file"
        : problem.state === "needs_fixing"
          ? "It didn't pass the plan checks"
          : "It needs your OK")
    : null;
  // Something is moving: an upload newer than what's published, or a draft not yet out.
  const inProgress =
    !!latest &&
    !problem &&
    !stoppedFirst &&
    !(
      status?.review_status === "published" &&
      // Internal upload number vs internal upload number (the status carries no
      // published file id); the plan number is display-only (D1).
      published?.version === latest.version
    );

  // --- the two CTAs ---
  const left = published
    ? {
        label: `${FLOW.openPublished} (${pubLabel(published)})`,
        onClick: () => onAction("briefing"),
        // Anyone in the organization reads the published briefing (board 01, 2026-10-31).
        disabled: false,
      }
    : { label: FLOW.noPublished, onClick: undefined, disabled: true };
  const step = row?.action;
  // D41/D46: nothing this viewer can open yet → a greyed View plan, "No plan yet".
  const nothingToView =
    !canUpload && !published && (!latest || stoppedFirst || !canOpen);
  const goToPlan: {
    label: string;
    onClick?: () => void;
    disabled: boolean;
    title?: string;
  } = nothingToView
    ? {
        label: FLOW.viewPlan,
        onClick: undefined,
        disabled: true,
        title: canOpen ? FLOW.noPlanYet : NO_ACCESS,
      }
    : { label: "Open account plan", onClick: openPlan, disabled: !canOpen };
  // A refused upload wasn't used (UI tour A item 2): its card stays, but the step
  // is the plan's own, as on the row.
  const right =
    problem && canUpload
      ? {
          label: FLOW.goToProblem,
          onClick: openProblem || onUpload,
          disabled: false,
        }
      : problem
        ? goToPlan
        : step &&
            (step.kind === "verify" ||
              step.kind === "publish" ||
              step.kind === "confirm" ||
              step.kind === "draft")
          ? {
              label: step.label,
              onClick: () => onAction(step.kind),
              disabled: false,
            }
          : inProgress
            ? goToPlan
            : canUpload && !pendingDraft
              ? {
                  // D41: the AE's own words, as on the row.
                  label: latest ? FLOW.updatePlan : FLOW.addPlan,
                  onClick: onUpload,
                  disabled: false,
                }
              : goToPlan;

  // F-UF-F23: a published plan with nothing in progress opens on its read-only
  // briefing either way, so "Open account plan" would repeat "Open published
  // plan": one button, not two to the same place.
  const sameAsLeft =
    right === goToPlan &&
    !!published &&
    !inProgress &&
    !problem &&
    !left.disabled;

  const headline = rejection
    ? row?.detail || `${FLOW.lastUploadRejected}: ${rejection.reason || ""}`
    : problemReason || row?.detail;

  return (
    <Dialog.Root open={!!account} onOpenChange={o => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-gray-900/30" />
        <Dialog.Content
          onOpenAutoFocus={e => {
            e.preventDefault();
            (e.currentTarget as HTMLElement).focus();
          }}
          tabIndex={-1}
          aria-modal="true"
          className="fixed bottom-0 right-0 top-0 z-50 flex w-full max-w-[560px] flex-col bg-white shadow-2xl focus:outline-none"
        >
          <div className="space-y-3 border-b border-gray-100 px-6 py-5">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <Dialog.Title className="truncate text-xl font-semibold text-gray-900">
                  {account?.customer_name}
                </Dialog.Title>
                {/* F-UF-F30: screen readers hear what the panel holds. */}
                <Dialog.Description className="sr-only">
                  {PANEL_DESCRIPTION}
                </Dialog.Description>
                {sub && <p className="mt-1 text-[13px] text-gray-500">{sub}</p>}
              </div>
              <Dialog.Close
                aria-label="Close"
                className="-mr-2 -mt-1 inline-flex h-11 w-11 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
              >
                <X className="h-[18px] w-[18px]" aria-hidden />
              </Dialog.Close>
            </div>
            {row && (
              <div className="flex flex-wrap items-center gap-2">
                <span
                  data-testid="panel-status"
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${BUCKET_STYLE[row.bucket].pill}`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${BUCKET_STYLE[row.bucket].dot}`}
                    aria-hidden
                  />
                  {BUCKET_LABEL[row.bucket]}
                </span>
                {headline && (
                  <span
                    className={`text-[13px] ${problem || rejection ? "font-medium text-red-700" : "text-gray-600"}`}
                  >
                    {headline}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            {/* Card 1: what the sponsor reads. */}
            {published ? (
              <section
                aria-label={FLOW.lastPublished}
                data-testid="panel-last-published"
                className={`${CARD} border-emerald-200 bg-emerald-50/50`}
              >
                <p className={`${KICKER} text-emerald-700`}>
                  {FLOW.lastPublished}
                </p>
                <h3 className="mt-0.5 text-base font-semibold text-gray-900">
                  {pubLabel(published)} · {shortDate(published.at)}
                </h3>
                <p className="mt-0.5 text-[13px] text-gray-700">
                  This is what the sponsor reads. Nothing below changes it until
                  {canUpload ? " you publish" : " the AE publishes"} again.
                </p>
                {status?.freshness && (
                  <p
                    data-testid="panel-refresh-due"
                    className={`mt-1 text-[13px] ${status.freshness.overdue ? "font-medium text-amber-700" : "text-gray-600"}`}
                  >
                    {dueLine(status.freshness)}
                  </p>
                )}
              </section>
            ) : (
              <section
                aria-label={FLOW.lastPublished}
                data-testid="panel-last-published"
                className={`${CARD} border-gray-200`}
              >
                <p className={`${KICKER} text-gray-400`}>
                  {FLOW.lastPublished}
                </p>
                <p className="mt-0.5 text-[13px] text-gray-700">
                  Nothing published yet. The sponsor sees no briefing.
                </p>
              </section>
            )}

            {/* Card 2: the newest upload's problem, where it stands, or nothing. */}
            {rejection ? (
              <section
                aria-label={FLOW.rejectedPreParse}
                data-testid="panel-problem"
                data-stage="pre-parse"
                className={`${CARD} border-red-200 bg-red-50/50`}
              >
                <p className={`${KICKER} text-red-700`}>
                  {FLOW.rejectedPreParse}
                </p>
                <h3 className="mt-0.5 text-base font-semibold text-gray-900">
                  {rejection.reason || "The file can't be used"}
                </h3>
                {rejection.message && (
                  <p className="mt-0.5 text-[13px] text-gray-700">
                    {rejection.message}
                  </p>
                )}
                <p className="mt-2 flex flex-wrap gap-1.5 text-xs text-gray-600">
                  {[
                    rejection.filename,
                    rejection.at ? shortDate(rejection.at) : null,
                    FLOW.fileNotKept,
                  ]
                    .filter(Boolean)
                    .map(fact => (
                      <span
                        key={fact as string}
                        className="max-w-full truncate rounded bg-white px-1.5 py-0.5 ring-1 ring-gray-200"
                      >
                        {fact}
                      </span>
                    ))}
                </p>
              </section>
            ) : problem ? (
              <section
                aria-label={PROBLEM_KIND[problem.state]}
                data-testid="panel-problem"
                className={`${CARD} border-red-200 bg-red-50/50`}
              >
                <p className={`${KICKER} text-red-700`}>
                  {PROBLEM_KIND[problem.state]}
                </p>
                <h3 className="mt-0.5 text-base font-semibold text-gray-900">
                  {problemReason}
                </h3>
                {problem.detail && (
                  <p className="mt-0.5 text-[13px] text-gray-700">
                    {problem.detail}
                  </p>
                )}
                <p className="mt-2 flex flex-wrap gap-1.5 text-xs text-gray-600">
                  {[
                    fileName,
                    latest?.created_at
                      ? `Uploaded ${shortDate(latest.created_at)}`
                      : null,
                    "No briefing written",
                  ]
                    .filter(Boolean)
                    .map(fact => (
                      <span
                        key={fact as string}
                        className="max-w-full truncate rounded bg-white px-1.5 py-0.5 ring-1 ring-gray-200"
                      >
                        {fact}
                      </span>
                    ))}
                </p>
              </section>
            ) : stoppedFirst ? (
              <section
                aria-label="In progress"
                data-testid="panel-in-progress"
                className={`${CARD} border-purple-200 bg-purple-50/40`}
              >
                <p className={`${KICKER} text-[#7E22CE]`}>In progress</p>
                <h3 className="mt-0.5 text-sm font-semibold text-gray-900">
                  {FLOW.aeReviewingUpload}
                </h3>
              </section>
            ) : inProgress && status ? (
              <section
                aria-label="In progress"
                data-testid="panel-in-progress"
                className={`${CARD} border-purple-200 bg-purple-50/40`}
              >
                <p className={`${KICKER} text-[#7E22CE]`}>In progress</p>
                {row?.detail && (
                  <h3 className="mt-0.5 text-sm font-semibold text-gray-900">
                    {row.detail.split(" · ")[0]}
                  </h3>
                )}
                <ol
                  aria-label="Plan progress"
                  className="mt-2 flex flex-wrap gap-1.5"
                >
                  {miniSteps(status, row).map(st => (
                    <li
                      key={st.label}
                      data-step={st.label}
                      data-state={st.state}
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${st.state === "done" ? "bg-emerald-50 text-emerald-700" : st.state === "now" ? "bg-[#9333EA] text-white" : "bg-gray-100 text-gray-500"}`}
                    >
                      {st.state === "done" && (
                        <Check
                          className="h-3 w-3"
                          strokeWidth={3}
                          aria-hidden
                        />
                      )}
                      {st.label}
                      <span className="sr-only">
                        {st.state === "done"
                          ? " · done"
                          : st.state === "now"
                            ? " · in progress"
                            : " · not started"}
                      </span>
                    </li>
                  ))}
                </ol>
              </section>
            ) : (
              <section
                aria-label="In progress"
                data-testid="panel-idle"
                className={`${CARD} border-gray-200`}
              >
                <p className={`${KICKER} text-gray-400`}>In progress</p>
                <p className="mt-0.5 text-[13px] text-gray-700">
                  Nothing in progress.
                </p>
              </section>
            )}

            {/* A confirmed draft isn't published (rule 2026-10-30): no upload until
                it is published or the AE replaces it on the plan page. */}
            {pendingDraft && (
              <section
                aria-label={REPLACE_DRAFT}
                data-testid="panel-pending-draft"
                className={`${CARD} border-amber-200 bg-amber-50/50`}
              >
                <p className="text-[13px] text-gray-800">
                  {pendingDraftNote(pendingDraft)}
                </p>
                {onReplaceDraft && canOpen && (
                  <button
                    type="button"
                    onClick={onReplaceDraft}
                    className="mt-2 inline-flex h-8 items-center rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                  >
                    {REPLACE_DRAFT}
                  </button>
                )}
              </section>
            )}

            {m && (
              <section aria-label="MEDDPICC" className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-900">
                  MEDDPICC
                </h3>
                <p className="flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-gray-600">
                  <span>
                    Economic Buyer{" "}
                    <span
                      className={
                        m.has_economic_buyer
                          ? "text-emerald-700"
                          : "font-medium text-amber-800"
                      }
                    >
                      {m.has_economic_buyer ? "Confirmed" : "Missing"}
                    </span>
                  </span>
                  <span>
                    Champion{" "}
                    <span
                      className={
                        m.has_champion
                          ? "text-emerald-700"
                          : "font-medium text-amber-800"
                      }
                    >
                      {m.has_champion ? "Confirmed" : "Missing"}
                    </span>
                  </span>
                  <span className="tabular-nums">
                    {m.tagged} of {m.total} stakeholders tagged
                  </span>
                </p>
              </section>
            )}

            {/* The AE's note from Confirm plan on the newest version (team only:
                the server leaves it out for anyone else). */}
            {overview && (
              <ConfirmNote
                file={overview.files.find(
                  f => f.id === overview.current_file_id
                )}
              />
            )}

            {/* Collapsed, information only: the plan page has the actions. */}
            {overview && (
              <VersionHistory
                files={
                  stoppedFirst
                    ? overview.files.filter(f => f.id !== latest?.id)
                    : overview.files
                }
                currentFileId={overview.current_file_id}
                journeys={journeys}
                latestProblem={
                  latest && problem ? { fileId: latest.id, problem } : null
                }
                rejectionIsProblem={canUpload}
                viewerConfirms={canUpload}
                rejections={
                  stoppedFirst
                    ? []
                    : (overview.rejected_uploads ??
                      (rejection
                        ? [{ ...rejection, by_name: null, dismissed: false }]
                        : []))
                }
                compact
                defaultOpen={false}
              />
            )}
          </div>

          {/* At most two actions. */}
          <div
            data-testid="panel-footer"
            className="flex items-center gap-2 border-t border-gray-100 px-6 pb-4 pt-4"
          >
            <button
              type="button"
              onClick={left.onClick}
              disabled={left.disabled}
              className={OUTLINED}
            >
              {left.label}
            </button>
            {/* Not on the account: the published briefing is all there is to open. */}
            {!sameAsLeft && (canOpen || !published) && (
              <button
                type="button"
                onClick={right.onClick}
                disabled={right.disabled}
                title={
                  "title" in right && right.title
                    ? right.title
                    : right.disabled
                      ? NO_ACCESS
                      : undefined
                }
                className={FILLED}
              >
                {right.label}
              </button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AccountPlanPanel;
